import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchSuggestionDetails,
  clearSuggestionDetails,
  selectData,
  selectIsLoading,
  selectSelectedSuggestion,
  selectSuggestionDetails,
  selectDetailsLoading,
  selectResponseType,
  fetchData,
  selectSuggestionsLoading,
  fetchSuggestions,
  setSuggestions,
  selectSuggestionsError,
  resetState,
  setChatbotId,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  selectOpenAiTokens,
} from "../../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotDetailsPanel,
  ChatbotForm,
  ChatbotInput,
  ChatbotButton,
  ChatbotExpandButton,
  ChatbotDetailsHeader,
  ChatbotError,
  ChatbotSuggestions,
  SuggestionsHeader,
  SuggestionsBox,
  SuggestionItem,
  SuggestionTitle,
  SuggestionDescription,
  ChatbotDetails,
  ChatbotMessage,
  AppTitle,
  LoadingElement,
} from "../styled";
import LoadingPage from "../../TechnicalTabs/LoadingPage";
import ErrorPage from "../../../../mainApp/features/TechnicalTabs/ErrorPage";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { handleUnauthorizedAccess } from "../../Utility/handleUnauthorizedAccess";
import { REELS_SUGGESTIONS_COST, REELS_DETAILS_COST } from "../../Utility/tokenCosts";

const Chatbot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const suggestionsLoading = useSelector(selectSuggestionsLoading);
  const suggestionsError = useSelector(selectSuggestionsError);
  const suggestions = useSelector(selectData);
  const selectedSuggestion = useSelector(selectSelectedSuggestion);
  const suggestionDetails = useSelector(selectSuggestionDetails);
  const detailsLoading = useSelector(selectDetailsLoading);
  const responseType = useSelector(selectResponseType);
  const [category, setCategory] = useState("");
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [openAiTokens, setOpenAiTokensState] = useState(0);
  const currentTokens = useSelector(selectOpenAiTokens);

  const validationToastId = 'reels-bot-validation-toast';

  // Funkcja opóźnienia
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(setChatbotId("reels"));
    dispatch(clearSuggestionDetails());
    dispatch(resetState());
    dispatch(fetchData());
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn("Dokument użytkownika nie istnieje.");
          }
          await delay(600); // Dodatkowe opóźnienie jako zabezpieczenie
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          await delay(600); // Opóźnienie w przypadku błędu
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }
  }, [restrictedDataError, navigate]);

  useEffect(() => {
    if (currentTokens !== openAiTokens) {
      setOpenAiTokensState(currentTokens);
    }
  }, [currentTokens]);

  const handleChange = (e) => setCategory(e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!category.trim()) {
      dispatch(setSuggestions("Proszę podać kategorię."));
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < REELS_SUGGESTIONS_COST) {
      toast.error("Masz za mało tokenów, aby wygenerować pomysły.", toastOptions);
      return;
    }

    dispatch(fetchSuggestions());
    dispatch({ type: "FETCH_VIDEO_SUGGESTIONS", payload: category });
    dispatch(clearSuggestionDetails());
  };

  const handleSuggestionClick = (index) => {
    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < REELS_DETAILS_COST) {
      toast.error("Masz za mało tokenów, aby zobaczyć szczegóły.", toastOptions);
      return;
    }

    if (selectedSuggestion === index) {
      dispatch(clearSuggestionDetails());
    } else {
      dispatch(fetchSuggestionDetails(index));
    }
  };

  const toggleDetailsPanel = () => setIsDetailsExpanded((prev) => !prev);

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage isHighZIndex={true} />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer isReels={true}>
            <ChatbotMain
              isDetailsExpanded={isDetailsExpanded}
              isReels={true}
              isDetailsPanelVisible={true}
            >
              <AppTitle isReels={true}>Social Media Reels</AppTitle>
              <ChatbotForm onSubmit={handleSubmit} isReels={true}>
                <ChatbotInput
                  type="text"
                  value={category}
                  onChange={handleChange}
                  placeholder="Wprowadź kategorię, np. fitness, moda"
                  isReels={true}
                />
                <ChatbotButton
                  type="submit"
                  disabled={suggestionsLoading}
                  isReels={true}
                  tokenCost={REELS_SUGGESTIONS_COST}
                >
                  Generuj pomysły
                </ChatbotButton>
              </ChatbotForm>
              {suggestionsLoading ? (
                <LoadingElement isReels={true}>Ładowanie</LoadingElement>
              ) : suggestionsError ? (
                <ChatbotError isReels={true}>{suggestionsError}</ChatbotError>
              ) : suggestions && responseType === "message" ? (
                <ChatbotMessage isSuggestions={true} isReels={true}>
                  {suggestions}
                </ChatbotMessage>
              ) : Array.isArray(suggestions) && suggestions.length > 0 && responseType === "suggestions" ? (
                <ChatbotSuggestions>
                  <SuggestionsHeader>Propozycje filmików:</SuggestionsHeader>
                  <SuggestionsBox>
                    {suggestions.map((suggestion, index) => (
                      <SuggestionItem
                        key={index}
                        onClick={() => handleSuggestionClick(index)}
                        tokenCost={REELS_DETAILS_COST}
                      >
                        <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                        <SuggestionDescription>
                          {suggestion.description}
                        </SuggestionDescription>
                      </SuggestionItem>
                    ))}
                  </SuggestionsBox>
                </ChatbotSuggestions>
              ) : (
                <ChatbotMessage isReels={true}>
                  Wprowadź kategorię, aby wygenerować pomysły.
                </ChatbotMessage>
              )}
            </ChatbotMain>
            <ChatbotDetailsPanel
              isDetailsExpanded={isDetailsExpanded}
              isReels={true}
            >
              <ChatbotDetailsHeader
                isDetailsExpanded={isDetailsExpanded}
                isReels={true}
              />
              <ChatbotExpandButton
                isDetailsExpanded={isDetailsExpanded}
                onClick={toggleDetailsPanel}
                isReels={true}
              >
                {isDetailsExpanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
              </ChatbotExpandButton>
              {selectedSuggestion !== null && suggestions.length > 0 ? (
                detailsLoading ? (
                  <LoadingElement>Ładowanie szczegółów</LoadingElement>
                ) : suggestionDetails ? (
                  <ChatbotDetails isReels={true}>
                    <SuggestionsHeader>
                      {suggestions[selectedSuggestion].title}
                    </SuggestionsHeader>
                    {suggestionDetails.split("\n").map((line, i) => {
                      // Usuwamy gwiazdki z tekstu
                      const cleanedLine = line.replace(/\*\*(.*?)\*\*/g, "$1");

                      // Sprawdzamy, czy linia to główny podpunkt (np. 1. Dlaczego warto podjąć wyzwanie?)
                      const subheadingMatch = cleanedLine.match(/^(\d+\.\s)(.+)/);
                      // Sprawdzamy, czy linia to inspiracja (np. - Anna (34 lata))
                      const inspirationMatch = cleanedLine.match(/^-\s(.+)/);

                      if (subheadingMatch) {
                        return (
                          <p key={i} className="subheading">
                            {cleanedLine}
                          </p>
                        );
                      } else if (inspirationMatch) {
                        return (
                          <p key={i} className="inspiration">
                            {cleanedLine}
                          </p>
                        );
                      }
                      return <p key={i}>{cleanedLine}</p>;
                    })}
                  </ChatbotDetails>
                ) : (
                  <ChatbotError>
                    Wystąpił błąd podczas ładowania szczegółów.
                  </ChatbotError>
                )
              ) : (
                <ChatbotMessage isReels={true}>
                  Wybierz pomysł, aby zobaczyć szczegóły.
                </ChatbotMessage>
              )}
            </ChatbotDetailsPanel>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default Chatbot;